/**
 * Plant Shade Module
 * Handles plant shade visualization and height indicators
 */

// Import plant database loader
import { getPlantDatabase } from './plantDatabaseLoader.js';

// Configuration
const HEIGHT_COLORS = {
    low: "#22cd0d ",     // Green (0-1 feet)
    medium: "#00FF00",  // Amber (1-3 feet)
    tall: "#daea33",    // Orange (3-6 feet)
    very_tall: "#FF8000", // Red (6+ feet)
    tall_tall: "#FF0000" // Red (9+ feet)
};

// Track whether shade visualization is enabled
let shadeVisualizationActive = false;
let heightIndicatorsActive = false; // Default to showing height indicators

// Function to initialize the shade and height visualization
function initPlantShadeVisualization() {
    // Add event listener for grid updates
    const confirmYesButton = document.getElementById('confirm-yes');
    if (confirmYesButton) {
        confirmYesButton.addEventListener('click', clearAllShadeElements);
    }
    
    // Add event listener to the combined toggle button
    const combinedToggleBtn = document.getElementById('toggle-combined-btn');
    if (combinedToggleBtn) {
        combinedToggleBtn.addEventListener('click', function() {
            toggleShadeVisualization();
            toggleHeightIndicators();
        });
    }
    
    // Add CSS for plant visualization
    addPlantVisualizationStyles();
    
    // Set up mutation observer to watch for new plants being added to grid
    setupGridObserver();
    
    // Apply to any existing plants in the grid
    applyVisualizationToExistingPlants();
}

// Add necessary CSS styles programmatically
function addPlantVisualizationStyles() {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
        /* Plant Height and Shade Visualization Styles */
        .plant-height-indicator-container {
            position: absolute;
            display: flex;
            flex-direction: column;
            width: 6px;
            height: 60px;
            bottom: 5px;
            right: 5px;
            opacity: 0.8;
            transition: opacity 0.2s;
            background-color: rgba(200, 200, 200, 0.2);
            border-radius: 4px;
            overflow: hidden;
        }
        
        .plant-height-indicator-container:hover {
            opacity: 1;
        }
        
        .plant-height-segment {
            width: 100%;
            height: 10px;
            transition: background-color 0.3s;
        }

        /* Height value display */
        .plant-height-value-display {
            position: absolute;
            top: 3px;
            right: 3px;
            background-color: rgba(0, 0, 0, 0.6);
            color: white;
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 10px;
            font-weight: bold;
            z-index: 3;
        }
        
        .plant-shade-radius {
            position: fixed;
            border-radius: 50%;
            pointer-events: none;
            z-index: 5;
            transform: translate(-50%, -50%);
            transition: opacity 0.3s;
        }
        
        .plant-shade-radius.hover-active {
            opacity: 0.7 !important;
            z-index: 10;
        }
        
        /* Make sure the plant is on top */
        .grid-item-5x5 {
            position: relative;
            z-index: 2;
        }
        
        .grid-item-5x5:hover {
            z-index: 10;
        }
        
        /* Design button active state */
        .design-btn.active {
            background-color: #ff69b4;
        }
    `;
    document.head.appendChild(styleElement);
}

// Toggle shade visualization
function toggleShadeVisualization() {
    const combinedToggleBtn = document.getElementById('toggle-combined-btn');
    
    shadeVisualizationActive = !shadeVisualizationActive;
    
    // Update button appearance
    if (shadeVisualizationActive || heightIndicatorsActive) {
        combinedToggleBtn.classList.add('active');
    } else {
        combinedToggleBtn.classList.remove('active');
    }
    
    // Update button text to reflect current state
    updateCombinedButtonText(combinedToggleBtn);
    
    // Update all grid items
    updateAllGridItemsVisualization();
}

// Toggle height indicators
function toggleHeightIndicators() {
    const combinedToggleBtn = document.getElementById('toggle-combined-btn');
    
    heightIndicatorsActive = !heightIndicatorsActive;
    
    // Update button appearance
    if (shadeVisualizationActive || heightIndicatorsActive) {
        combinedToggleBtn.classList.add('active');
    } else {
        combinedToggleBtn.classList.remove('active');
    }
    
    // Update button text to reflect current state
    updateCombinedButtonText(combinedToggleBtn);
    
    // Update all grid items
    updateAllGridItemsVisualization();
}

// Helper function to update the combined button text
function updateCombinedButtonText(button) {
    if (shadeVisualizationActive && heightIndicatorsActive) {
        button.innerHTML = '<i class="fa fa-sun-o"></i> <i class="fa fa-arrows-v"></i> Hide Plant Shades';
    } else if (shadeVisualizationActive) {
        button.innerHTML = '<i class="fa fa-sun-o"></i> <i class="fa fa-arrows-v"></i> Shade Only';
    } else if (heightIndicatorsActive) {
        button.innerHTML = '<i class="fa fa-sun-o"></i> <i class="fa fa-arrows-v"></i> Height Only';
    } else {
        button.innerHTML = '<i class="fa fa-sun-o"></i> <i class="fa fa-arrows-v"></i> Show Shade & Height';
    }
}

// Function to create height indicator for a plant (db from caller)
function createHeightIndicator(plantName, db) {
    if (!plantName || !db || !db[plantName]) return null;
    const heightValue = db[plantName].heightValue || 1;
    
    // Create container for the segments
    const heightContainer = document.createElement('div');
    heightContainer.className = 'plant-height-indicator-container';
    
    // Define segment count and max height for scaling
    const totalSegments = 5;
    const maxHeight = 20; // Feet
    
    // Calculate how many segments should be colored
    const filledSegments = Math.min(Math.ceil((heightValue / maxHeight) * totalSegments), totalSegments);
    
    // Create all segments from bottom to top (reversed display order)
    for (let i = totalSegments; i > 0; i--) {
        const segment = document.createElement('div');
        segment.className = 'plant-height-segment';
        
        // Set color based on segment position
        if (i <= filledSegments) {
            if (i <= 1) {
                segment.style.backgroundColor = HEIGHT_COLORS.low;
            } else if (i <= 2) {
                segment.style.backgroundColor = HEIGHT_COLORS.medium;
            } else if (i <= 3) {
                segment.style.backgroundColor = HEIGHT_COLORS.tall;
            } else {
                segment.style.backgroundColor = HEIGHT_COLORS.very_tall;
            }
        } else {
            segment.style.backgroundColor = 'transparent';
        }
        
        heightContainer.appendChild(segment);
    }
    
    heightContainer.title = `Height: ${db[plantName].height || 'Unknown height'}`;
    return heightContainer;
}

// Function to create height value display (db from caller)
function createHeightValueDisplay(plantName, db) {
    if (!plantName || !db || !db[plantName]) return null;
    const heightValue = db[plantName].heightValue || 1;
    
    // Create container for height display
    const heightDisplay = document.createElement('div');
    heightDisplay.className = 'plant-height-value-display';
    
    // Set height value with feet symbol
    heightDisplay.textContent = heightValue.toFixed(1) + 'ft';
    
    return heightDisplay;
}

// Function to create shade radius visualization (db from caller)
function createShadeRadius(plantName, db) {
    if (!plantName || !db || !db[plantName]) return null;
    const shadeRadius = db[plantName].shadeRadius || 0;
    const shadeType = db[plantName].shadeCreation || 'light';
    
    // Skip if no shade
    if (shadeRadius <= 0) return null;
    
    // Create shade element
    const shadeElement = document.createElement('div');
    shadeElement.className = 'plant-shade-radius';
    
    // Set size based on radius (convert to pixels)
    const radiusPixels = shadeRadius * 80; // Assuming grid-item is about 80px
    shadeElement.style.width = `${radiusPixels * 2}px`;
    shadeElement.style.height = `${radiusPixels * 2}px`;
    
    // Set opacity based on shade type
    let opacity;
    switch (shadeType) {
        case 'very light':
            opacity = 0.5;
            break;
        case 'light':
            opacity = 0.5;
            break;
        case 'moderate':
            opacity = 0.5;
            break;
        case 'heavy':
            opacity = 0.5;
            break;
        default:
            opacity = 0.5;
    }
    
    shadeElement.style.backgroundColor = `rgba(0, 0, 0, ${opacity})`;
    
    // Add tooltip information
    shadeElement.title = `Shade radius: ${shadeRadius} ft - ${shadeType} shade`;
    
    return shadeElement;
}

// Function to clear all shade radius elements
function clearAllShadeElements() {
    document.querySelectorAll('.plant-shade-radius').forEach(el => el.remove());
}

// Apply visualization to a single grid item (loads DB on first use)
async function applyVisualizationToGridItem(gridItem) {
    const existingHeight = gridItem.querySelector('.plant-height-indicator-container');
    const existingHeightValue = gridItem.querySelector('.plant-height-value-display');
    if (existingHeight) existingHeight.remove();
    if (existingHeightValue) existingHeightValue.remove();
    const plantLabel = gridItem.querySelector('.plant-label');
    if (!plantLabel) return;
    const displayName = plantLabel.textContent.trim();
    const plantNameForDb =
        (plantLabel.dataset.basePlant && String(plantLabel.dataset.basePlant).trim()) || displayName;
    const db = await getPlantDatabase();
    if (heightIndicatorsActive) {
        const heightIndicator = createHeightIndicator(plantNameForDb, db);
        if (heightIndicator) gridItem.appendChild(heightIndicator);
        const heightValueDisplay = createHeightValueDisplay(plantNameForDb, db);
        if (heightValueDisplay) gridItem.appendChild(heightValueDisplay);
    }
    if (shadeVisualizationActive) {
        document.querySelectorAll(`.plant-shade-radius[data-grid-item="${gridItem.dataset.gridPosition}"]`)
            .forEach(el => el.remove());
        const shadeRadius = createShadeRadius(plantNameForDb, db);
        if (shadeRadius) {
            // Set a data attribute to identify which grid item this shade belongs to
            if (!gridItem.dataset.gridPosition) {
                gridItem.dataset.gridPosition = `pos-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            }
            shadeRadius.dataset.gridItem = gridItem.dataset.gridPosition;
            
            // Position the shade element correctly
            const rect = gridItem.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            
            shadeRadius.style.left = `${centerX}px`;
            shadeRadius.style.top = `${centerY}px`;
            
            // Add to body for proper layering
            document.body.appendChild(shadeRadius);
            
            // Add repositioning on scroll and resize
            window.addEventListener('scroll', updateShadePosition);
            window.addEventListener('resize', updateShadePosition);
            
            // Add hover event for shade visualization
            gridItem.addEventListener('mouseenter', () => {
                if (shadeVisualizationActive) {
                    const hoverShade = document.querySelector(`.plant-shade-radius[data-grid-item="${gridItem.dataset.gridPosition}"]`);
                    if (hoverShade) {
                        hoverShade.classList.add('hover-active');
                    }
                }
            });
            
            gridItem.addEventListener('mouseleave', () => {
                const hoverShade = document.querySelector(`.plant-shade-radius[data-grid-item="${gridItem.dataset.gridPosition}"]`);
                if (hoverShade) {
                    hoverShade.classList.remove('hover-active');
                }
            });
        }
    } else {
        // Remove shade elements if visualization is disabled
        if (gridItem.dataset.gridPosition) {
            document.querySelectorAll(`.plant-shade-radius[data-grid-item="${gridItem.dataset.gridPosition}"]`)
                .forEach(el => el.remove());
        }
    }
}

// Update shade element positions on scroll/resize
function updateShadePosition() {
    const gridItems = document.querySelectorAll('.grid-item-5x5[data-grid-position]');
    
    gridItems.forEach(gridItem => {
        const shadeElement = document.querySelector(`.plant-shade-radius[data-grid-item="${gridItem.dataset.gridPosition}"]`);
        
        if (shadeElement) {
            const rect = gridItem.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            
            shadeElement.style.left = `${centerX}px`;
            shadeElement.style.top = `${centerY}px`;
        }
    });
}

// Update all grid items with current visualization settings
function updateAllGridItemsVisualization() {
    // Clear all existing shade elements
    document.querySelectorAll('.plant-shade-radius').forEach(el => el.remove());
    
    const gridItems = document.querySelectorAll('.grid-item-5x5');
    gridItems.forEach(gridItem => {
        applyVisualizationToGridItem(gridItem);
    });
}

// Apply visualization to existing plants in the grid
function applyVisualizationToExistingPlants() {
    const gridItems = document.querySelectorAll('.grid-item-5x5');
    gridItems.forEach(gridItem => {
        const plantLabel = gridItem.querySelector('.plant-label');
        if (plantLabel) {
            applyVisualizationToGridItem(gridItem);
        }
    });
}

// Setup observer to watch for changes to the grid (new plants added)
function setupGridObserver() {
    const grid = document.getElementById('grid');
    if (!grid) return;
    
    // Create a mutation observer to watch for changes
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                // New elements were added
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1 && node.classList.contains('grid-item-5x5')) {
                        // New grid item was added, wait a moment for plant to be added
                        setTimeout(() => {
                            applyVisualizationToGridItem(node);
                        }, 100);
                    }
                });
            } else if (mutation.type === 'attributes' && mutation.target.classList.contains('grid-item-5x5')) {
                // Grid item was modified (plant was added or changed)
                setTimeout(() => {
                    applyVisualizationToGridItem(mutation.target);
                }, 100);
            }
        });
    });
    
    // Start observing the grid with the configured parameters
    observer.observe(grid, { 
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
    });
}

// Main initialization function
export function initializePlantShade() {
    // Initialize the shade and height visualization
    initPlantShadeVisualization();
    
    console.log('✅ Plant shade functionality initialized');
}

// Export additional functions that might be needed by other modules
export {
    clearAllShadeElements,
    toggleShadeVisualization,
    toggleHeightIndicators,
    updateAllGridItemsVisualization,
    applyVisualizationToGridItem
}; 